/**
 * Video latency — a safety readout, not decoration.
 *
 * Our video path is HLS wherever WebRTC cannot be reached, which runs 2–6
 * seconds behind the aircraft. At 3 m/s that is 9–18 m between where
 * the picture shows the aircraft and where it actually is, so an operator who
 * treats the picture as a control reference is flying a position that expired
 * seconds ago. The number therefore has to be ON the picture, permanently, and
 * it has to be MEASURED — a constant that looks like a measurement is worse than
 * no number at all, because it survives the day the stream gets slower.
 *
 * What can honestly be measured in a browser, and what cannot:
 *
 * - **Measurable:** how far the player's playhead sits behind the live edge of
 *   the stream it is playing. hls.js reports it directly (`hls.latency`), and
 *   without it the same distance falls out of the media element's own seekable
 *   or buffered range. On the WebRTC path there is no playhead to compare —
 *   `getStats()` gives the same delay as its parts instead (`estimateWebrtcLatency`).
 * - **NOT measurable:** the camera, the encoder, the uplink and the packager.
 *   Nothing in the browser can see them.
 *
 * So every number this module produces is an UNDER-estimate of the true
 * glass-to-glass delay, and it is labelled `EST` wherever it is shown. When even
 * the player-side distance cannot be read, the badge says so rather than
 * inventing a figure.
 */

import { MANUAL_LIMITS } from './manual-vector';

/**
 * Below this the picture is close enough to the aircraft to command from.
 *
 * 400 ms is the same order as the setpoint dead-man (`TTL_HOLD_MS`): a picture
 * that trails by less than one dead-man interval is showing the result of the
 * command currently in force. Nothing we ship today reaches it — HLS cannot —
 * but WebRTC will, and it must not need a redesign to say so.
 */
export const CONTROL_GRADE_BELOW_MS = 400;

/**
 * Above this the picture is not a delayed view, it is a record.
 *
 * Our HLS path sits at 2-6 s when it is healthy, so 8 s is the first figure
 * that cannot be explained by a working stream: past it the player is stalling,
 * rebuffering or drifting, and the number keeps growing. It needs its own band
 * because a stalled stream and a healthy one used to be told apart only by
 * comparing a figure against a baseline the operator had to carry in their
 * head — which is the opposite of reading the situation in three seconds.
 */
export const PICTURE_STALE_ABOVE_MS = 8_000;

/**
 * Ground speed at or below which the aircraft counts as STOPPED, m/s.
 *
 * The same 0.5 m/s floor `pictureLeadM` refuses to quote a lead below, and for
 * the same reason: under it the reading is GPS noise rather than motion.
 */
export const STOPPED_GROUND_SPEED = 0.5;

/**
 * What has to happen after the operator lets go, before the picture can catch up.
 *
 * `HOVER` is set OPTIMISTICALLY, the instant the stop is dispatched and before
 * the POST resolves, so the state change is not the moment the aircraft stopped
 * moving. Between the two sit the command's own trip (COP -> API -> MQTT -> RC
 * -> SDK, bounded here by the setpoint dead-man the RC honours) and the
 * aircraft's deceleration from up to `MAX_SPEED_MS`. Only then does the video's
 * own delay start counting down.
 *
 * Used only when telemetry cannot confirm the stop — see `settleDurationMs`.
 */
export const COMMAND_TRIP_ALLOWANCE_MS = MANUAL_LIMITS.TTL_HOLD_MS;

/** 5 m/s shed at a conservative 2.5 m/s^2. Deliberately generous: settling early is the failure. */
export const DECELERATION_ALLOWANCE_MS = 2_000;

/** Has telemetry actually shown the aircraft stopped? Unknown speed is not a stop. */
export function aircraftStopped(groundSpeedMs: number | null | undefined): boolean {
  return typeof groundSpeedMs === 'number'
    && Number.isFinite(groundSpeedMs)
    && Math.abs(groundSpeedMs) <= STOPPED_GROUND_SPEED;
}

/** Where a measurement came from. Shown to the operator, so it is never a guess. */
export type VideoLatencySource =
  | 'hls-live-edge'
  | 'media-seekable'
  | 'media-buffered'
  | 'webrtc-stats';

export interface VideoLatencyEstimate {
  /** Distance from the live edge to the playhead, ms. Always ≥ 0. */
  latencyMs: number;
  source: VideoLatencySource;
}

/** Longest player-side latency worth believing, ms. Beyond it the read is junk. */
const MAX_CREDIBLE_LATENCY_MS = 120_000;

function finite(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Player state → an estimate, or null when nothing usable could be read.
 *
 * Preference order is by directness: hls.js computes the edge distance from the
 * playlist it is actually loading; `seekable` is the media element's own view of
 * the same window; `buffered` is the weakest of the three (it says how much has
 * been downloaded, which only equals the edge distance while the player is
 * keeping up) and is used last.
 *
 * Null is returned rather than zero for "could not read". They are different
 * facts, and only one of them means the picture is live.
 */
export function estimateVideoLatency(input: {
  /** `hls.latency`, seconds. Absent for native playback and before the first playlist. */
  hlsLatencySeconds?: number | null;
  currentTime?: number | null;
  /** End of the last seekable range, seconds. */
  seekableEnd?: number | null;
  /** End of the last buffered range, seconds. */
  bufferedEnd?: number | null;
}): VideoLatencyEstimate | null {
  const hlsSeconds = finite(input.hlsLatencySeconds);
  // hls.js answers 0 before the first playlist is loaded, which is the one
  // number this must never forward: 0 ms would read as CONTROL GRADE on a
  // stream that has not started.
  if (hlsSeconds !== null && hlsSeconds > 0) {
    return credible(hlsSeconds * 1000, 'hls-live-edge');
  }

  const currentTime = finite(input.currentTime);
  if (currentTime === null) return null;

  const seekableEnd = finite(input.seekableEnd);
  if (seekableEnd !== null && seekableEnd > currentTime) {
    return credible((seekableEnd - currentTime) * 1000, 'media-seekable');
  }

  const bufferedEnd = finite(input.bufferedEnd);
  if (bufferedEnd !== null && bufferedEnd > currentTime) {
    return credible((bufferedEnd - currentTime) * 1000, 'media-buffered');
  }

  return null;
}

function credible(latencyMs: number, source: VideoLatencySource): VideoLatencyEstimate | null {
  if (!Number.isFinite(latencyMs) || latencyMs < 0) return null;
  if (latencyMs > MAX_CREDIBLE_LATENCY_MS) return null;
  return { latencyMs, source };
}

/**
 * The counters `estimateWebrtcLatency` reads, lifted off `getStats()`.
 *
 * A WebRTC picture arrives as a `MediaStream`, which has no `seekable` and no
 * `buffered` range — so none of the three sources above can see it, and
 * `estimateVideoLatency` would answer null for a sub-second stream exactly as
 * readily as for a broken one. `getStats()` is the only surface that exposes
 * where the time actually goes.
 *
 * Every field is nullable because a browser reports what it reports: Firefox
 * and Safari have each shipped without one or another of these, and a missing
 * counter must narrow the estimate rather than crash it.
 */
export interface WebrtcStatsSample {
  /** `inbound-rtp.jitterBufferDelay`, seconds. CUMULATIVE since the connection opened. */
  jitterBufferDelaySeconds: number | null;
  /** `inbound-rtp.jitterBufferEmittedCount`, frames. CUMULATIVE. */
  jitterBufferEmittedCount: number | null;
  /** `inbound-rtp.totalDecodeTime`, seconds. CUMULATIVE. */
  totalDecodeTimeSeconds: number | null;
  /** `inbound-rtp.framesDecoded`, frames. CUMULATIVE. */
  framesDecoded: number | null;
  /** `candidate-pair.currentRoundTripTime`, seconds. Already instantaneous. */
  currentRoundTripTimeSeconds: number | null;
}

function numberField(stat: Record<string, unknown>, key: string): number | null {
  const value = stat[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * The one video `inbound-rtp` entry and the live `candidate-pair`, flattened.
 *
 * Takes a plain iterable rather than an `RTCStatsReport` so it tests with object
 * literals — the caller passes `report.values()`. Every field is read
 * defensively: `getStats()` is a bag of untyped dictionaries whose membership
 * varies by browser and by version, and a missing key must narrow the estimate
 * rather than throw inside a 1 Hz interval.
 *
 * One video stream is assumed, which is what a `recvonly` transceiver against a
 * single MediaMTX mount produces; the first video `inbound-rtp` wins. The
 * candidate pair is chosen by how much the browser vouches for it — nominated
 * and succeeded beats succeeded, which beats anything else carrying an RTT —
 * because a pair that lost the ICE race still reports the RTT it measured
 * before it lost, and that is not the path the picture is arriving on.
 */
export function webrtcStatsSample(stats: Iterable<unknown>): WebrtcStatsSample {
  const sample: WebrtcStatsSample = {
    jitterBufferDelaySeconds: null,
    jitterBufferEmittedCount: null,
    totalDecodeTimeSeconds: null,
    framesDecoded: null,
    currentRoundTripTimeSeconds: null,
  };
  let inboundFound = false;
  let bestPairRank = 0;

  for (const raw of stats) {
    if (typeof raw !== 'object' || raw === null) continue;
    const stat = raw as Record<string, unknown>;

    if (
      !inboundFound &&
      stat.type === 'inbound-rtp' &&
      // `kind` is the current spec field; `mediaType` is the legacy name still
      // emitted by older Chromium and by Safari.
      (stat.kind === 'video' || stat.mediaType === 'video')
    ) {
      inboundFound = true;
      sample.jitterBufferDelaySeconds = numberField(stat, 'jitterBufferDelay');
      sample.jitterBufferEmittedCount = numberField(stat, 'jitterBufferEmittedCount');
      sample.totalDecodeTimeSeconds = numberField(stat, 'totalDecodeTime');
      sample.framesDecoded = numberField(stat, 'framesDecoded');
      continue;
    }

    if (stat.type === 'candidate-pair') {
      const rtt = numberField(stat, 'currentRoundTripTime');
      if (rtt === null) continue;
      const succeeded = stat.state === 'succeeded';
      const rank = succeeded && stat.nominated === true ? 3 : succeeded ? 2 : 1;
      if (rank > bestPairRank) {
        bestPairRank = rank;
        sample.currentRoundTripTimeSeconds = rtt;
      }
    }
  }

  return sample;
}

/**
 * Mean per frame across the window between two samples of a cumulative pair.
 *
 * The counters are lifetime totals, so dividing them outright gives the average
 * since the connection opened — which smooths away the degradation an operator
 * needs to see. Differencing two samples gives the mean over the last second
 * instead, which is what `hls.latency` reports for the other transport.
 *
 * Null when the window cannot be measured: no previous sample to difference
 * against (the caller falls back to the lifetime mean for that one tick), a
 * counter that went backwards (a renegotiated connection resets them), or zero
 * frames emitted in the window.
 */
function windowMean(
  totalNow: number | null,
  countNow: number | null,
  totalPrev: number | null,
  countPrev: number | null,
): number | null {
  if (totalNow === null || countNow === null) return null;
  if (totalPrev === null || countPrev === null) return null;
  const frames = countNow - countPrev;
  const seconds = totalNow - totalPrev;
  if (frames <= 0 || seconds < 0) return null;
  return seconds / frames;
}

/** Lifetime mean of a cumulative total/count pair — the first tick's only option. */
function lifetimeMean(total: number | null, count: number | null): number | null {
  if (total === null || count === null) return null;
  // Zero frames is the pre-roll state, and 0/0 must not become a 0 ms reading:
  // that would render as CONTROL GRADE on a stream with no picture yet.
  if (count <= 0 || total < 0) return null;
  return total / count;
}

/**
 * How far a WebRTC picture is behind the aircraft, as far as the browser can see.
 *
 * Three terms, all of them player-side, summed:
 *
 * - **Jitter buffer** — how long a frame waits before it is handed to the
 *   decoder. Normally the dominant term, and the direct analogue of the HLS
 *   path's distance behind the live edge.
 * - **Decode** — the decoder's own time per frame.
 * - **Half the round trip** — the one-way network delay the frame already spent
 *   in flight. Halved because RTT measures the trip out AND back, and only the
 *   inbound leg is behind this picture.
 *
 * Same honesty caveat as everything else in this module, and if anything a
 * stronger one: the camera, the encoder and the RTMP uplink into MediaMTX are
 * all upstream of the first packet this can see, so the figure is an
 * UNDER-estimate of glass-to-glass and is labelled `EST` like the rest.
 *
 * The jitter buffer is REQUIRED — with no frame having ever left it there is no
 * picture to be late, and a figure built from RTT alone would be a network
 * measurement wearing a video label. Decode and RTT are optional addends.
 *
 * Returns null while frames are not moving. A frozen WebRTC stream has no
 * growing quantity to read (unlike HLS, where the live edge runs away from a
 * stalled playhead), so "not measured" is the true answer rather than a stale
 * mean that would keep reporting the healthy number the freeze interrupted.
 */
export function estimateWebrtcLatency(
  current: WebrtcStatsSample,
  previous: WebrtcStatsSample | null,
): VideoLatencyEstimate | null {
  const jitterSeconds = previous
    ? windowMean(
        current.jitterBufferDelaySeconds,
        current.jitterBufferEmittedCount,
        previous.jitterBufferDelaySeconds,
        previous.jitterBufferEmittedCount,
      )
    : lifetimeMean(current.jitterBufferDelaySeconds, current.jitterBufferEmittedCount);
  if (jitterSeconds === null) return null;

  const decodeSeconds =
    (previous
      ? windowMean(
          current.totalDecodeTimeSeconds,
          current.framesDecoded,
          previous.totalDecodeTimeSeconds,
          previous.framesDecoded,
        )
      : lifetimeMean(current.totalDecodeTimeSeconds, current.framesDecoded)) ?? 0;

  const rtt = finite(current.currentRoundTripTimeSeconds);
  const oneWaySeconds = rtt !== null && rtt >= 0 ? rtt / 2 : 0;

  const totalSeconds = jitterSeconds + decodeSeconds + oneWaySeconds;
  // The same zero this module refuses from hls.js before its first playlist,
  // and refused here for the identical reason: 0 ms renders as CONTROL GRADE,
  // the one reading that must never be emitted for a picture nothing has
  // measured. A browser reporting `jitterBufferEmittedCount` advancing while
  // `jitterBufferDelay` stays 0, with decode and RTT absent, would otherwise
  // land exactly there.
  if (totalSeconds <= 0) return null;

  return credible(totalSeconds * 1000, 'webrtc-stats');
}

/**
 * `240 ms` under a second, `+3.2 s` above it.
 *
 * The `+` is deliberate on the seconds form and absent on the milliseconds one:
 * seconds of delay are an offset an operator has to hold in their head, and
 * signing it says which way it runs.
 */
export function formatVideoLatency(latencyMs: number): string {
  if (!Number.isFinite(latencyMs) || latencyMs < 0) return '—';
  if (latencyMs < 1000) return `${Math.round(latencyMs)} ms`;
  return `+${(latencyMs / 1000).toFixed(1)} s`;
}

/**
 * Four bands, because three of them are different situations and the fourth is
 * the absence of a measurement.
 *
 * `situational` is the cautious default — an unknown latency is never control
 * grade — and `stale` exists so a stream that has stopped keeping up does not
 * render in the same colour and the same words as one that is merely delayed.
 */
export type VideoLatencyGrade = 'control' | 'situational' | 'stale' | 'unknown';

export interface VideoLatencyLabel {
  grade: VideoLatencyGrade;
  /** `VIDEO +3.2 s EST` / `VIDEO 240 ms EST` / `VIDEO LATENCY NOT MEASURED`. */
  headline: string;
  /** The consequence, which is the part that matters at 02:00. */
  consequence: string;
  /** One sentence saying what was measured and what was not. */
  detail: string;
}

const SITUATIONAL = 'SITUATIONAL ONLY, NOT A CONTROL REFERENCE';
/** Not a caveat any more. At this delay the picture is evidence, not awareness. */
const STALE = 'PICTURE STALE — DO NOT USE';

const SOURCE_DETAIL: Record<VideoLatencySource, string> = {
  'hls-live-edge': "the player's distance behind the live edge of the stream",
  'media-seekable': "the media element's seekable window",
  'media-buffered': "the media element's buffered range",
  'webrtc-stats': "the WebRTC jitter buffer, decode time and half the round trip",
};

/**
 * The badge's words. Same element, same position, whichever answer applies —
 * so a WebRTC path that lands under the threshold changes the text and nothing
 * else.
 */
export function videoLatencyLabel(estimate: VideoLatencyEstimate | null): VideoLatencyLabel {
  if (!estimate) {
    return {
      grade: 'unknown',
      headline: 'VIDEO LATENCY NOT MEASURED',
      consequence: SITUATIONAL,
      detail:
        'Nothing in this player could be read for a latency figure, so none is shown. The picture is still behind the aircraft by an unknown amount.',
    };
  }

  const grade: VideoLatencyGrade =
    estimate.latencyMs < CONTROL_GRADE_BELOW_MS
      ? 'control'
      : estimate.latencyMs > PICTURE_STALE_ABOVE_MS
        ? 'stale'
        : 'situational';
  const bounded = `Estimated from ${SOURCE_DETAIL[estimate.source]}. It does not include the camera, encoder or uplink, so the real delay is at least this.`;
  return {
    grade,
    headline: `VIDEO ${formatVideoLatency(estimate.latencyMs)} EST`,
    consequence: grade === 'control' ? 'CONTROL GRADE' : grade === 'stale' ? STALE : SITUATIONAL,
    detail:
      grade === 'stale'
        ? `${bounded} At more than ${PICTURE_STALE_ABOVE_MS / 1000} s behind, this is a record of where the aircraft was, not a view of where it is.`
        : bounded,
  };
}

/**
 * How far the aircraft has travelled since the picture was taken, metres.
 *
 * The whole reason the badge exists, made concrete for the aircraft actually
 * being flown rather than left as a worked example in a document. Null when
 * either half is unknown, or when the aircraft is slow enough that the lead is
 * inside GPS noise and quoting it would be false precision.
 */
export function pictureLeadM(
  latencyMs: number | null,
  groundSpeedMs: number | null | undefined,
): number | null {
  const latency = finite(latencyMs);
  const speed = finite(groundSpeedMs ?? null);
  if (latency === null || speed === null || speed <= 0.5) return null;
  const metres = (speed * latency) / 1000;
  return metres < 1 ? null : metres;
}

/**
 * What the picture is doing relative to the aircraft.
 *
 * This is the only safe working method on a 3-second picture: manoeuvre, stop,
 * look. The phases name each step of it, so an operator knows whether what they
 * are looking at has caught up with what they commanded.
 */
export type PictureMotionPhase = 'trailing' | 'settling' | 'current';

export const PICTURE_PHASE_TEXT: Record<PictureMotionPhase, string> = {
  trailing: 'PICTURE TRAILS THE AIRCRAFT',
  settling: 'PICTURE SETTLING…',
  current: 'PICTURE CURRENT',
};

/**
 * `PICTURE SETTLING…` while the commanded stop is still working through the
 * pipe, and — only when the latency was actually measured — `PICTURE CURRENT`
 * afterwards. With no measurement there is no moment to promise, so the phase
 * stays `settling` and the caller renders the reason instead of a claim.
 *
 * `stopConfirmed` is what makes the figure honest. The caller normally starts
 * this clock from a TELEMETRY frame showing the aircraft actually stopped, and
 * then the only thing left to wait for is the video's own delay — that is the
 * `true` case. When telemetry cannot say (no ground speed on this aircraft),
 * the clock has to start from the optimistic `HOVER` instead, which is BEFORE
 * the command reached the RC and before the aircraft finished decelerating, so
 * both of those are added. Erring long costs the operator a second of caution;
 * erring short tells them a moving picture has caught up.
 */
export function settleDurationMs(
  estimate: VideoLatencyEstimate | null,
  { stopConfirmed = false }: { stopConfirmed?: boolean } = {},
): number | null {
  if (!estimate) return null;
  const allowance = stopConfirmed ? 0 : COMMAND_TRIP_ALLOWANCE_MS + DECELERATION_ALLOWANCE_MS;
  return Math.max(0, estimate.latencyMs) + allowance;
}
